/** Endpoints #31-#32 — server-persisted UI state (§12.4). */

import type { UiStateResponse } from '@lw/shared';
import { apiJson, apiVoid, isNotFoundError } from './client';

function statePath(key: string): string {
  return `/ui-state/${encodeURIComponent(key)}`;
}

/**
 * #31 `GET /api/ui-state/:key`. A `404` means "never persisted", which every caller treats as
 * "use the default" (§12.4) — so it resolves to `null` instead of throwing.
 */
export async function getUiState<T = unknown>(
  key: string,
  signal?: AbortSignal,
): Promise<T | null> {
  try {
    const response = await apiJson<UiStateResponse>(statePath(key), { signal });
    return response.value as T;
  } catch (error) {
    if (isNotFoundError(error)) return null;
    throw error;
  }
}

/** #32 `PUT /api/ui-state/:key` — arbitrary JSON ≤16 KB; callers debounce (500 ms, §12.3). */
export function putUiState(key: string, value: unknown): Promise<void> {
  return apiVoid(statePath(key), { method: 'PUT', body: value });
}
